import { connectRange } from 'react-instantsearch-core';
import { RangeSlider, RangeDataType } from '$shared/components/RangeSlider';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    StyledButtonsContainer,
    StyledInputsContainer,
    StyledInputsDashSpacer,
    StyledRangeContainer,
    StyledSliderContainer,
    StyledInputBox,
    StyledInputLabel,
    StyledInput,
    StyledInputCurrency,
} from './styled';
import { useFranchise, useTranslation } from '~/shared/utils';
import { Button } from '~/shared/components';
import { useFilterUIState } from '~/features/productsList/hooks';
import { RangeRefinement } from '~/features/productsList/models';

export type RangeType = {
    currentRefinement: RangeRefinement;
    refine: (value?: RangeRefinement) => void;
    min: number;
    max: number;
    step: number;
    name: string;
    attribute: string;
};

const CustomRangeInput = ({ currentRefinement, min, max, step, refine, name }: RangeType) => {
    const [currentRange, setCurrentRange] = useState<RangeRefinement>(currentRefinement);

    const { activeMarket } = useFranchise();
    const { translate } = useTranslation();
    const { setMinPrice, setMaxPrice } = useFilterUIState(({ setMinPrice, setMaxPrice }) => ({
        setMinPrice,
        setMaxPrice,
    }));

    const minValRef = useRef<HTMLInputElement>(null);
    const maxValRef = useRef<HTMLInputElement>(null);

    const onChange = useCallback((value: RangeDataType) => {
        setCurrentRange(value);
    }, []);

    const applyRange = () => {
        refine(currentRange);
    };

    const clearRange = () => {
        refine({
            min: min,
            max: max,
        });
        setCurrentRange({
            min: min,
            max: max,
        });
    };

    useEffect(() => {
        setMinPrice(min);
        setMaxPrice(max);
    }, [min, max, setMinPrice, setMaxPrice]);

    useEffect(() => {
        setCurrentRange(currentRefinement);
    }, [currentRefinement]);

    return (
        <StyledRangeContainer>
            <StyledInputsContainer>
                <StyledInputBox>
                    <StyledInputLabel as="label" variant="caption">
                        {translate('commerce.filters.min')}
                    </StyledInputLabel>
                    <StyledInput
                        type="number"
                        name={`${name}-min`}
                        ref={minValRef}
                        min={min}
                        max={max}
                        step={step}
                        aria-label={`${name}-min`}
                        value={currentRange.min || ''}
                        onChange={(event) =>
                            onChange({
                                ...currentRange,
                                min: event.currentTarget.valueAsNumber,
                            })
                        }
                    />
                    <StyledInputCurrency>
                        {activeMarket?.currency?.toLocaleLowerCase()}
                    </StyledInputCurrency>
                </StyledInputBox>
                <StyledInputsDashSpacer />
                <StyledInputBox>
                    <StyledInputLabel as="label" variant="caption">
                        {translate('commerce.filters.max')}
                    </StyledInputLabel>
                    <StyledInput
                        type="number"
                        name={`${name}-max`}
                        ref={maxValRef}
                        min={min}
                        max={max}
                        step={step}
                        aria-label={`${name}-max`}
                        value={currentRange.max || ''}
                        onChange={(event) =>
                            onChange({
                                ...currentRange,
                                max: event.currentTarget.valueAsNumber,
                            })
                        }
                    />
                    <StyledInputCurrency>
                        {activeMarket?.currency?.toLocaleLowerCase()}
                    </StyledInputCurrency>
                </StyledInputBox>
            </StyledInputsContainer>
            <StyledSliderContainer>
                <RangeSlider
                    min={min}
                    max={max}
                    value={currentRange}
                    step={step}
                    onChange={onChange}
                />
            </StyledSliderContainer>
            <StyledButtonsContainer>
                <Button onClick={clearRange} size="sm" variant="secondary">
                    {translate('commerce.filters.reset')}
                </Button>
                <Button onClick={applyRange} size="sm" variant="primary">
                    {translate('commerce.filters.apply')}
                </Button>
            </StyledButtonsContainer>
        </StyledRangeContainer>
    );
};

export const FilterRange = React.memo(connectRange(CustomRangeInput));
